import { Group, Select, Stack, Title } from '@mantine/core';
import { useEffect, useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';

import { useBudget } from '../hooks/useBudget';
import { BudgetSummary } from './BudgetSummary';

export interface StoreBudgetReportProps {
	storeCode: string;
	storeName: string;
	fiscalYear?: number;
	periodNumber?: number;
}

export function StoreBudgetReport({
	storeCode,
	storeName,
	fiscalYear,
	periodNumber,
}: StoreBudgetReportProps) {
	const { t } = useTranslation('store');
	const {
		currentFiscalPeriod,
		selectedFiscalYear,
		setSelectedFiscalYear,
		selectedPeriodNumber,
		setSelectedPeriodNumber,
		budgetData,
		fiscalYearList,
		hasData,
	} = useBudget(storeCode, fiscalYear, periodNumber);

	const fiscalYearOptions =
		fiscalYearList && hasData
			? fiscalYearList?.map(x => ({
					value: x.fiscalYear.toString(),
					label: t('budgets.storeBudgetReport.fiscalYearOption', {
						fiscalYear: x.fiscalYear.toString(),
					}),
				}))
			: [];

	const periodOptions = useMemo(() => {
		return hasData
			? budgetData
					.sort((a, b) => a.periodNumber - b.periodNumber)
					.map(x => ({
						value: x.periodNumber.toString(),
						label: x.fiscalPeriod?.periodName ?? '',
					}))
			: [{ value: '', label: t('common:pagination:empty') }];
	}, [hasData, budgetData, t]);

	useEffect(() => {
		if (currentFiscalPeriod.fiscalYear == Number(selectedFiscalYear)) {
			setSelectedPeriodNumber(currentFiscalPeriod.periodNumber.toString());
			return;
		}
		setSelectedPeriodNumber(periodOptions[0]?.value ?? '');
	}, [currentFiscalPeriod, periodOptions, selectedFiscalYear, setSelectedPeriodNumber]);

	return (
		<Stack mt='xs'>
			<Group grow justify='space-between'>
				<Title order={2}>{storeName}</Title>
				<Group justify='flex-end' wrap='nowrap'>
					<Group align='flex-start' justify='flex-end'>
						<Select
							allowDeselect={false}
							value={selectedFiscalYear}
							data={fiscalYearOptions}
							onChange={value => setSelectedFiscalYear(value)}
						/>
						<Select
							value={selectedPeriodNumber}
							data={periodOptions}
							onChange={value => setSelectedPeriodNumber(value)}
						/>
					</Group>
				</Group>
			</Group>
			<BudgetSummary
				fiscalYear={Number(selectedFiscalYear)}
				periodNumber={Number(selectedPeriodNumber)}
				singleStoreCustomerCode={storeCode}
				budgetItem={budgetData.find(
					x =>
						x.fiscalPeriod?.fiscalYear === Number(selectedFiscalYear) &&
						x.fiscalPeriod.periodNumber === Number(selectedPeriodNumber),
				)}
				budgetItems={budgetData}
			/>
		</Stack>
	);
}
